/**
 * Document service for PDF generation, viewing, and HTML preview
 */
import { API_URL } from '../config';
import { getAuthToken } from '../utils/auth';

/**
 * Document types supported by the API
 * @typedef {'invoice' | 'estimate' | 'quote'} DocumentType
 */

/**
 * Service for document-related API calls
 */
class DocumentService {
  /**
   * Download a PDF document
   * @param {DocumentType} documentType - Type of document (invoice, estimate, quote)
   * @param {number} id - Document ID
   * @returns {Promise<Blob>} - PDF blob that can be saved
   */
  async downloadPdf(documentType, id) {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_URL}/documents/${documentType}/${id}/download`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to download ${documentType}`);
    }

    return await response.blob();
  }

  /**
   * Get a URL for viewing a PDF document inline
   * @param {DocumentType} documentType - Type of document (invoice, estimate, quote)
   * @param {number} id - Document ID
   * @returns {string} - URL for viewing the PDF
   */
  getPdfViewUrl(documentType, id) {
    return `${API_URL}/documents/${documentType}/${id}/view`;
  }

  /**
   * Get HTML preview of a document
   * @param {DocumentType} documentType - Type of document (invoice, estimate, quote)
   * @param {number} id - Document ID
   * @returns {Promise<string>} - HTML content
   */
  async getHtmlPreview(documentType, id) {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_URL}/documents/${documentType}/${id}/preview`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to get ${documentType} preview`);
    }

    return await response.text();
  }

  /**
   * Open a PDF for viewing in a new window
   * @param {DocumentType} documentType - Type of document (invoice, estimate, quote)
   * @param {number} id - Document ID
   */
  viewPdfInNewWindow(documentType, id) {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    // Create a URL with the token as a query parameter
    const viewUrl = `${this.getPdfViewUrl(documentType, id)}?token=${encodeURIComponent(token)}`;
    window.open(viewUrl, '_blank');
  }

  /**
   * Download a PDF and save it with a filename
   * @param {DocumentType} documentType - Type of document (invoice, estimate, quote)
   * @param {number} id - Document ID
   * @param {string} [filename] - Optional filename (defaults to documentType_id.pdf)
   */
  async downloadPdfFile(documentType, id, filename) {
    try {
      const blob = await this.downloadPdf(documentType, id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `${documentType}_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error(`Error downloading ${documentType}:`, error);
      throw error;
    }
  }

  /**
   * Show HTML preview in a popup window
   * @param {DocumentType} documentType - Type of document (invoice, estimate, quote)
   * @param {number} id - Document ID
   */
  async previewInPopup(documentType, id) {
    try {
      const html = await this.getHtmlPreview(documentType, id);
      const previewWindow = window.open('', '_blank', 'width=800,height=600');
      if (previewWindow) {
        previewWindow.document.write(html);
        previewWindow.document.close();
      } else {
        alert('Please allow popups to view the preview');
      }
    } catch (error) {
      console.error(`Error previewing ${documentType}:`, error);
      throw error;
    }
  }

  /**
   * Send document via email
   * @param {DocumentType} documentType - Type of document (invoice, estimate, quote)
   * @param {number} id - Document ID
   * @param {string} email - Customer email address
   * @param {string} [customerName] - Optional customer name
   * @returns {Promise<void>}
   */
  async sendEmail(documentType, id, email, customerName) {
    const token = getAuthToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_URL}/documents/${documentType}/${id}/send-email`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        customerName
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to send ${documentType} via email`);
    }

    const data = await response.json();
    return data;
  }
}

export const documentService = new DocumentService(); 